import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/api/client';
import type { Page } from '@/api/types';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Icon } from '@/components/Icon';
import { ViewSidebar } from '@/components/ViewSidebar';
import { Button, IconButton } from '@/components/Button';
import { Tag } from '@/components/Tag';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { legacyHref } from '@/shell/nav';
import { date, money, num, qty } from '@/lib/format';
import { Applied, Chips, DataTable, Denied, FilterButton, k, Pager, PhoneBar, SearchBox, Sentinel, type Col } from './ListKit';
import { FilterSheet } from './FilterSheet';
import { Arrive, PurchaseCard, poTotals, poViews, purchaseChips, purchaseProgressText } from './PurchaseCards';
import { PurchaseOrderDetail } from './PurchaseOrderDetail';
import { PurchaseCreateDialog } from './PurchaseCreateDialog';
import { daysAgo, usePagedList, usePurchaseCounts, useSuppliers, useUrlState, useWarehouses } from './api';
import { purchaseStatus } from './status';
import type { PurchaseOrder } from './types';
import s from './PurchaseOrdersPage.module.css';

const RANGES = [
  { key: '7', label: '近 7 天下单', days: 7 },
  { key: '30', label: '近 30 天下单', days: 30 },
  { key: '90', label: '近 90 天下单', days: 90 },
];

const EMPTY: Record<string, [string, string]> = {
  all: ['还没有采购单', '可以新建采购单，也可以先看「采购建议」里的缺口。'],
  ready: ['没有待到货采购单', '已下单与部分入库的采购单会出现在这里。'],
  ORDERED: ['没有等待到货的采购单', '已下单的采购单都到货了。'],
  PARTIALLY_RECEIVED: ['没有部分入库的采购单', '分批到货的采购单会出现在这里。'],
  RECEIVED: ['没有已入库的采购单', '全部到货并转入库后会出现在这里。'],
  DRAFT: ['没有草稿', ''],
  CANCELLED: ['没有已取消的采购单', ''],
};

function csvCell(value: string | number) {
  const text = String(value);
  const safe = typeof value === 'string' && /^\s*[=+@-]/.test(text) ? `'${text}` : text;
  return `"${safe.replaceAll('"', '""')}"`;
}

/**
 * 采购单列表（老系统 /purchase-orders）。
 * - 手机：卡片 + 无限滚动，点卡片进详情页（深层页隐藏 Tab 栏）。
 * - iPad：列表栏 + 详情分栏（P2），选中写进 ?id=。
 * - 桌面：表格 + 分页，点行进详情页。
 * 视图 = 状态（接口 status 参数），与「采购建议」排在同一排 chips；供应商 / 仓库 / 下单时间进「筛选」。
 */
export function PurchaseOrdersPage() {
  const ff = useFormFactor();
  const nav = useNavigate();
  const can = useSession((st) => st.can);
  const allowed = can('purchase.read');
  const [sp, set] = useUrlState();
  const view = sp.get('view') || 'ready';
  const overdueOnly = sp.get('overdue') === '1' && view === 'ready';
  const q = sp.get('q') || '';
  const supplierId = sp.get('supplierId') || undefined;
  const warehouseId = sp.get('warehouseId') || undefined;
  const range = sp.get('range') || undefined;
  const page = Number(sp.get('page')) || 1;
  const selectedId = sp.get('id') || undefined;
  const arrivalSort = sp.get('arrivalSort') === 'desc' ? 'desc' : 'asc';
  const phone = ff === 'phone';
  const desktop = ff === 'desktop';
  const pageSize = desktop ? 30 : 20;
  const [panel, setPanel] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [searching, setSearching] = useState(!!q);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState('');

  const rangeDef = RANGES.find((r) => r.key === range);
  const base = useMemo(
    () => ({ keyword: q || undefined, supplierId, warehouseId, from: rangeDef ? daysAgo(rangeDef.days) : undefined }),
    [q, supplierId, warehouseId, rangeDef],
  );
  const list = usePagedList<PurchaseOrder, { pendingValue: number }>(allowed ? (view === 'ready' ? '/api/v2/purchase-orders/ready' : '/api/purchase-orders') : null, { ...base, status: view === 'all' || view === 'ready' ? undefined : view, arrivalSort: view === 'ready' ? arrivalSort : undefined, overdue: overdueOnly ? 1 : undefined }, { pageSize, append: !desktop, page });
  const { counts, reload: reloadCounts } = usePurchaseCounts(base, allowed);
  const suppliers = useSuppliers(allowed);
  const warehouses = useWarehouses(allowed);

  if (!allowed) return <Denied what="查看采购单" perm="查看采购订单" />;

  const viewLabel = poViews.find((v) => v.key === view)?.label || '采购单';
  const chips = purchaseChips({
    active: view,
    counts,
    canSuggest: can('purchase.suggestions.read'),
    canPO: true,
    pick: (key) => (key === 'suggest' ? nav('/purchase-suggestions') : set({ view: key === 'ready' ? undefined : key, overdue: undefined, page: undefined, id: undefined })),
  }).filter((chip) => ff === 'desktop' || chip.key !== 'suggest');
  const filterCount = Number(!!supplierId) + Number(!!warehouseId) + Number(!!rangeDef);
  const canCreate = can('purchase.create');
  const writable = import.meta.env.VITE_READONLY === '0';
  const createDialog = createOpen && <PurchaseCreateDialog suppliers={suppliers} warehouses={warehouses} onClose={() => setCreateOpen(false)} onCreated={() => { list.reload(); reloadCounts(); }} />;
  const showMoney = list.items.some((o) => o.totalAmount !== undefined);
  const activeId = selectedId || list.items[0]?.id;

  async function exportOrders() {
    if (exportBusy) return;
    setExportBusy(true);
    setExportError('');
    try {
      const path = view === 'ready' ? '/api/v2/purchase-orders/ready' : '/api/purchase-orders';
      const all: PurchaseOrder[] = [];
      for (let next = 1; ; next += 1) {
        const result = await api.get<Page<PurchaseOrder>>(path, {
          ...base,
          status: view === 'all' || view === 'ready' ? undefined : view,
          arrivalSort: view === 'ready' ? arrivalSort : undefined,
          overdue: overdueOnly ? 1 : undefined,
          page: next,
          pageSize: 100,
        });
        all.push(...result.items);
        if (!result.pagination.hasNext) break;
      }
      const header = ['采购单号', '下单日期', '供应商', '仓库', '状态', '预计到货', '采购明细', '采购量', '已入库', '未入库', ...(showMoney ? ['总金额', '未入库货值'] : [])];
      const rows = all.map((order) => {
        const totals = poTotals(order.lines);
        return [order.purchaseNo, date(order.orderDate), order.supplier.name, order.warehouse.name, purchaseStatus[order.status]?.label || order.status,
          date(order.expectedArrivalDate), order.lines.map((line) => `${line.product.name} ×${qty(line.quantity)}`).join('、'),
          qty(totals.ordered), qty(totals.received), qty(totals.pending),
          ...(showMoney ? [order.totalAmount === undefined ? '' : money(order.totalAmount), totals.pendingValue === undefined ? '' : money(totals.pendingValue)] : [])];
      });
      const content = [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
      const url = URL.createObjectURL(new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `采购单-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : '导出失败');
    } finally {
      setExportBusy(false);
    }
  }

  const applied = (
    <Applied
      items={[
        ...(overdueOnly ? [{ key: 'overdue', label: '逾期未到货', onRemove: () => set({ overdue: undefined, page: undefined }) }] : []),
        ...(supplierId ? [{ key: 'sup', label: suppliers.find((x) => x.id === supplierId)?.name || '指定供应商', onRemove: () => set({ supplierId: undefined, page: undefined }) }] : []),
        ...(warehouseId ? [{ key: 'wh', label: warehouses.find((w) => w.id === warehouseId)?.name || '指定仓库', onRemove: () => set({ warehouseId: undefined, page: undefined }) }] : []),
        ...(rangeDef ? [{ key: 'range', label: rangeDef.label, onRemove: () => set({ range: undefined, page: undefined }) }] : []),
      ]}
      onClear={() => set({ supplierId: undefined, warehouseId: undefined, range: undefined, overdue: undefined, page: undefined })}
    />
  );

  const search = (cls?: string) => (
    <SearchBox className={cls} value={q} autoFocus={phone && searching && !q} placeholder="采购单号 / 供应商 / 产品 / 备注" onSearch={(v) => set({ q: v || undefined, page: undefined, id: undefined })} />
  );

  const panelEl = panel && (
    <FilterSheet
      sheet={phone}
      value={{ supplierId, warehouseId, range }}
      onClose={() => setPanel(false)}
      onApply={(f) => {
        setPanel(false);
        set({ supplierId: f.supplierId, warehouseId: f.warehouseId, range: f.range, page: undefined, id: undefined });
      }}
      groups={[
        { key: 'supplierId', title: '供应商', opts: suppliers.map((x) => ({ value: x.id, label: x.name })) },
        { key: 'warehouseId', title: '入库仓库', opts: warehouses.map((w) => ({ value: w.id, label: w.name })) },
        { key: 'range', title: '采购日期', opts: RANGES.map((r) => ({ value: r.key, label: r.label })) },
      ]}
      note="状态在上方切换；列表按创建时间倒序。"
    />
  );

  const newPO = canCreate && (writable
    ? <button type="button" className={`${k.linkBtn} ${k.primaryLink}`} onClick={() => setCreateOpen(true)}><Icon name="plus" />新建采购单</button>
    : <a href={legacyHref('/purchase-orders')} className={`${k.linkBtn} ${k.primaryLink}`} title="只读体验期：新建采购单在老版进行"><Icon name="plus" />新建采购单（老版）</a>);

  const body = (() => {
    if (list.error && !list.items.length) {
      return <ErrorState title="采购单加载失败" message={list.error.message} detail="GET /api/purchase-orders" onRetry={list.reload} />;
    }
    if (list.loading && !list.items.length) return <div className={k.pad}><Skeleton /><Skeleton /><Skeleton lines={3} /></div>;
    if (!list.items.length) {
      const filtered = q || filterCount;
      const [title, hint] = EMPTY[view] || EMPTY.all;
      return <Empty icon="inbox" title={filtered ? '没有符合条件的采购单' : title}>{filtered ? '换个关键词，或者清空筛选再看看。' : hint}</Empty>;
    }
    return null;
  })();

  const cards = (
    <>
      {body ||
        list.items.map((o) => (
          <PurchaseCard key={o.id} po={o} selected={!phone && o.id === activeId} to={phone ? `/purchase-orders/${o.id}` : `/purchase-orders?${withId(sp, o.id)}`} />
        ))}
      <Sentinel hasNext={list.hasNext} loading={list.loading} count={list.items.length} onMore={list.loadMore} />
    </>
  );

  /* ------------------------------ 手机 ------------------------------ */
  if (phone) {
    return (
      <div className={k.phone}>
        <PhoneBar
          title="采购"
          back={{ label: '应用', to: '/apps', fallback: '/apps' }}
          actions={
            <>
              <IconButton name="srch" label="搜索" onClick={() => setSearching((x) => !x)} />
              {canCreate && (writable ? <button type="button" className={k.pIcon} onClick={() => setCreateOpen(true)} aria-label="新建采购单"><Icon name="plus" size={22} /></button> : <a className={k.pIcon} href={legacyHref('/purchase-orders')} aria-label="新建采购单（老版）"><Icon name="plus" size={22} /></a>)}
            </>
          }
        />
        {(searching || q) && <div className={k.psearch}>{search()}</div>}
        <div className={k.chipbar}>
          <Chips items={chips} inset="phone" />
          <FilterButton count={filterCount} onClick={() => setPanel(true)} />
        </div>
        {applied}
        <div className={k.pbody}>
          <div className={k.listbar}><span>{list.total} 张{view === 'ready' && list.stats?.pendingValue !== undefined && ` · 在途货值 ${money(list.stats.pendingValue)}`}{q && ` · 搜索「${q}」`}</span>{view === 'ready' && <button type="button" className={s.arrivalSort} onClick={() => set({ arrivalSort: arrivalSort === 'asc' ? 'desc' : undefined, page: undefined, id: undefined })} aria-label={`按预计到货时间${arrivalSort === 'asc' ? '从晚到早' : '从早到晚'}排序`}>预计到货 <Icon name="sort" size={14} /></button>}</div>
          {cards}
        </div>
        {panelEl}
        {createDialog}
      </div>
    );
  }

  /* ------------------------------ iPad：列表 + 详情 ------------------------------ */
  if (!desktop) {
    return (
      <div className={k.split}>
        <section className={k.listCol}>
          <header className={k.lh}>
            <h1>{viewLabel}<small className="tnum">{list.total}</small></h1>
            {canCreate && (writable ? <button type="button" className={k.pIcon} onClick={() => setCreateOpen(true)} aria-label="新建采购单"><Icon name="plus" size={20} /></button> : <a className={k.pIcon} href={legacyHref('/purchase-orders')} aria-label="新建采购单（老版）" title="新建采购单（老版）"><Icon name="plus" size={20} /></a>)}
          </header>
          <div className={k.lsearch}>{search()}<FilterButton boxed count={filterCount} onClick={() => setPanel(true)} /></div>
          <Chips items={chips} inset="list" />
          {applied}
          <div className={k.lbody}>{cards}</div>
        </section>
        <div className={k.divider} />
        <section className={k.detailCol}>
          {activeId ? <PurchaseOrderDetail key={activeId} id={activeId} embedded onDeleted={() => { list.reload(); set({ id: undefined }); }} /> : <Empty icon="cards" title="选择左侧采购单查看详情">详情在这里打开，不用跳页。</Empty>}
        </section>
        {panelEl}
        {createDialog}
      </div>
    );
  }

  /* ------------------------------ 桌面：表格 ------------------------------ */
  const cols: Col<PurchaseOrder>[] = [
    { key: 'no', title: '采购单号', w: 178, fix: true, render: (o) => <div className={s.cell}><b className="tnum">{o.purchaseNo}</b><small>{date(o.orderDate)} 下单</small></div> },
    { key: 'supplier', title: '供应商 / 仓库', w: 175, render: (o) => <div className={s.cell}><b>{o.supplier.name}</b><small>{o.warehouse.name}</small></div> },
    { key: 'status', title: '状态 / 到货', w: 150, render: (o) => { const st = purchaseStatus[o.status]; return <div className={s.cell}><Tag tone={st?.tone || 'none'} sm>{st?.label || o.status}</Tag><small>{o.expectedArrivalDate ? `预计 ${date(o.expectedArrivalDate)}` : '未设置到货日'} <Arrive po={o} /></small></div>; } },
    { key: 'lines', title: '明细', w: 220, render: (o) => <div className={s.cell}>{o.lines.slice(0, 2).map((l) => <span key={l.id}>{l.product.name} ×{qty(l.quantity)}</span>)}{o.lines.length > 2 && <small>等 {o.lines.length} 项</small>}</div> },
    { key: 'progress', title: '入库进度', w: 124, render: (o) => { const t = poTotals(o.lines); return <div className={s.cell}><b className="tnum">{qty(t.received)} / {qty(t.ordered)}</b><small>{purchaseProgressText(o)}</small></div>; } },
    ...(showMoney ? [
      { key: 'amount', title: '总金额', w: 116, align: 'r', render: (o) => o.totalAmount === undefined ? '—' : money(o.totalAmount) },
      { key: 'pending', title: '未入库货值', w: 124, align: 'r', render: (o) => { const v = poTotals(o.lines).pendingValue; return v === undefined ? '—' : money(v); } },
    ] as Col<PurchaseOrder>[] : []),
    { key: 'action', title: '操作', w: 74, render: () => <span className={s.viewLink}>查看 ›</span> },
  ];
  const pendingSum = view === 'ready' && list.stats?.pendingValue !== undefined
    ? num(list.stats.pendingValue)
    : list.items.reduce((a, o) => a + (o.status === 'CANCELLED' ? 0 : num(poTotals(o.lines).pendingValue)), 0);
  const pendingLabel = view === 'ready' ? '在途货值' : '本页未入库货值';
  const viewDesc = view === 'ready' ? '已向供应商下单，尚未入库或部分入库的采购单'
    : view === 'PARTIALLY_RECEIVED' ? '已开始入库，仍有货品待到货的采购单'
      : view === 'RECEIVED' ? '已完成入库的采购单'
        : view === 'DRAFT' ? '尚未下单的采购单'
          : view === 'CANCELLED' ? '已取消的采购单' : '全部采购单';

  return (
    <div className={`${k.desk} ${k.desktopPage}`}>
      <header className={k.desktopHead}>
        <div><h1>{viewLabel}</h1><p>{viewDesc} <span>共 {list.total} 条{showMoney && pendingSum > 0 ? ` · ${pendingLabel} ${money(pendingSum)}` : ''}</span></p></div>
        <div className={k.desktopActions}><Button icon="list">列设置</Button>{newPO}</div>
      </header>
      <div className={k.desktopWork}>
        <ViewSidebar title="采购视图" active={view} onPick={(key) => set({ view: key === 'ready' ? undefined : key, page: undefined, id: undefined })} views={[
          ...(can('purchase.suggestions.read') ? [{ key: 'suggest', label: '待下单建议', icon: 'inbox', to: '/purchase-suggestions' }] : []),
          { key: 'ready', label: '已下单待到货', icon: 'inbox', count: (counts.ORDERED ?? 0) + (counts.PARTIALLY_RECEIVED ?? 0) },
          { key: 'PARTIALLY_RECEIVED', label: '部分入库', icon: 'truck', count: counts.PARTIALLY_RECEIVED },
          { key: 'DRAFT', label: '草稿', icon: 'flag', count: counts.DRAFT, separator: true },
          { key: 'RECEIVED', label: '已入库', icon: 'box', count: counts.RECEIVED },
          { key: 'CANCELLED', label: '已取消', icon: 'x', count: counts.CANCELLED },
          { key: 'all', label: '全部采购单', icon: 'cards', count: counts.all, separator: true },
          { key: 'suppliers', label: '供应商', icon: 'team', to: '/suppliers' },
        ]} />
        <section className={k.desktopArea}>
          <div className={k.desktopToolbar}>
            {search()}
            <select
              className={k.desktopQuickFilter}
              aria-label="仓库"
              value={warehouseId || ''}
              onChange={(event) => set({ warehouseId: event.target.value || undefined, page: undefined, id: undefined })}
            >
              <option value="">仓库：全部</option>
              {warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
            </select>
            <span className={k.sp} />
            <Button icon="down" disabled={exportBusy || list.loading || !list.total} onClick={exportOrders}>{exportBusy ? '导出中…' : '导出'}</Button>
          </div>
          {exportError && <p className={s.exportError} role="alert">{exportError}</p>}
          {applied}
          {body ? <div className={k.dbody}>{body}</div> : <DataTable cols={cols} rows={list.items} rowKey={(o) => o.id} onRowClick={(o) => nav(`/purchase-orders/${o.id}`)} />}
          {!body && <Pager page={list.page} pageSize={pageSize} total={list.total} shown={list.items.length} totalPages={list.totalPages} onGo={(p) => set({ page: p > 1 ? String(p) : undefined })} extra={showMoney && pendingSum > 0 ? <span className="em tnum">{pendingLabel} {money(pendingSum)}</span> : undefined} />}
        </section>
      </div>
      {panelEl}
      {createDialog}
    </div>
  );
}

function withId(sp: URLSearchParams, id: string) {
  const n = new URLSearchParams(sp);
  n.set('id', id);
  return n.toString();
}
